import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ForceChangePasswordPage from './pages/ForceChangePasswordPage';
import DashboardPage from './pages/DashboardPage';
import WorkersPage from './pages/WorkersPage';
import AdvancesPage from './pages/AdvancesPage';
import MonthlyGridPage from './pages/MonthlyGridPage';
import ReportsPage from './pages/ReportsPage';
import RemuneracionesPage from './pages/RemuneracionesPage';
import BranchesPage from './pages/BranchesPage';
import UsersPage from './pages/UsersPage';
import SettingsPage from './pages/SettingsPage';
import OperativeMonthPage from './pages/OperativeMonthPage';
import OvertimePage from './pages/OvertimePage';
import StaffingPage from './pages/StaffingPage';
import LaborCostPage from './pages/LaborCostPage';
import ShiftsPageV2 from './pages/ShiftsPageV2';
import BankAccountsPage from './pages/BankAccountsPage';
import AttendanceMarkPage from './pages/AttendanceMarkPage';
import AttendanceAdminPage from './pages/AttendanceAdminPage';
import './App.css';

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, loading, isAdmin, mustChangePassword } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Force password change if required
  if (mustChangePassword) {
    return <Navigate to="/change-password" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, mustChangePassword } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="spinner"></div>
      </div>
    );
  }

  if (isAuthenticated && !mustChangePassword) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const AppRoutes = () => {
  const { isAuthenticated, mustChangePassword, loading } = useAuth();
  
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={<ResetPasswordPage />}
      />
      <Route
        path="/change-password"
        element={
          isAuthenticated && mustChangePassword ? (
            <ForceChangePasswordPage />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/anticipos"
        element={
          <ProtectedRoute>
            <AdvancesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/planilla"
        element={
          <ProtectedRoute>
            <MonthlyGridPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trabajadores"
        element={
          <ProtectedRoute>
            <WorkersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reportes"
        element={
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/remuneraciones"
        element={
          <ProtectedRoute>
            <RemuneracionesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sucursales"
        element={
          <ProtectedRoute adminOnly>
            <BranchesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/usuarios"
        element={
          <ProtectedRoute adminOnly>
            <UsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/configuracion"
        element={
          <ProtectedRoute adminOnly>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mes-operativo"
        element={
          <ProtectedRoute adminOnly>
            <OperativeMonthPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/horas-extras"
        element={
          <ProtectedRoute>
            <OvertimePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dotacion"
        element={
          <ProtectedRoute adminOnly>
            <StaffingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/costo-laboral"
        element={
          <ProtectedRoute adminOnly>
            <LaborCostPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/turnos"
        element={
          <ProtectedRoute>
            <ShiftsPageV2 />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cuentas-bancarias"
        element={
          <ProtectedRoute>
            <BankAccountsPage />
          </ProtectedRoute>
        }
      />
      <Route path="/asistencia-admin"
        element={
          <ProtectedRoute>
            <AttendanceAdminPage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public attendance - completely outside AuthProvider */}
        <Route path="/asistencia/:qrToken" element={<AttendanceMarkPage />} />
        <Route path="*" element={
          <AuthProvider>
            <AppRoutes />
            <Toaster />
          </AuthProvider>
        } />
      </Routes>
      <Toaster />
    </BrowserRouter>
  );
}

export default App;
